import { supabase } from '../lib/supabase';

export interface SportStat {
  id: string;
  team_id: string;
  player_card_id: string;
  stat_key: string;
  stat_value: number;
  date: string;
  game_id?: string;
  opponent?: string;
  season?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Service for managing sport-specific statistics
 */
class SportStatsService {
  /**
   * Get all stats for a player card
   */
  async getStats(playerCardId: string, teamId: string): Promise<SportStat[]> {
    const { data, error } = await supabase
      .from('sport_stats')
      .select('*')
      .eq('player_card_id', playerCardId)
      .eq('team_id', teamId)
      .order('date', { ascending: false });

    if (error) {
      console.error('[SportStatsService] Error fetching stats:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Add a new stat
   */
  async addStat(
    playerCardId: string,
    teamId: string,
    statKey: string,
    statValue: number,
    date: string,
    gameId?: string,
    opponent?: string,
    season?: string
  ): Promise<SportStat> {
    // If gameId is provided, use it; otherwise use opponent for grouping
    const insertData: any = {
      player_card_id: playerCardId,
      team_id: teamId,
      stat_key: statKey,
      stat_value: statValue,
      date: date,
      game_id: gameId || null,
      opponent: opponent || null,
      season: season || null,
    };

    const { data, error } = await supabase
      .from('sport_stats')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error('[SportStatsService] Error adding stat:', error);
      throw error;
    }

    return data;
  }

  /**
   * Update an existing stat
   */
  async updateStat(
    statId: string,
    updates: {
      stat_value?: number;
      date?: string;
      game_id?: string;
      season?: string;
    }
  ): Promise<SportStat> {
    const { data, error } = await supabase
      .from('sport_stats')
      .update(updates)
      .eq('id', statId)
      .select()
      .single();

    if (error) {
      console.error('[SportStatsService] Error updating stat:', error);
      throw error;
    }

    return data;
  }

  /**
   * Delete a stat
   */
  async deleteStat(statId: string): Promise<void> {
    const { error } = await supabase
      .from('sport_stats')
      .delete()
      .eq('id', statId);

    if (error) {
      console.error('[SportStatsService] Error deleting stat:', error);
      throw error;
    }
  }

  /**
   * Delete all sport_stats for a game (e.g. before replacing with GameChanger import).
   */
  async deleteStatsByGameId(gameId: string): Promise<void> {
    const { error } = await supabase
      .from('sport_stats')
      .delete()
      .eq('game_id', gameId);

    if (error) {
      console.error('[SportStatsService] Error deleting stats by game:', error);
      throw error;
    }
  }

  /**
   * Save ordered plate appearance results for a player in a game (preserves PA order when editing).
   */
  async saveGameBattingSequence(
    gameId: string,
    playerCardId: string,
    teamId: string,
    date: string,
    results: string[]
  ): Promise<void> {
    const { error } = await supabase
      .from('game_batting_sequence')
      .upsert(
        {
          game_id: gameId,
          player_card_id: playerCardId,
          team_id: teamId,
          results: results,
          date: date,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'game_id,player_card_id' }
      );

    if (error) {
      console.error('[SportStatsService] Error saving game batting sequence:', error);
      throw error;
    }
  }

  /**
   * Get ordered plate appearance results for a player in a game, or null if none stored.
   */
  async getGameBattingSequence(
    gameId: string,
    playerCardId: string
  ): Promise<string[] | null> {
    const { data, error } = await supabase
      .from('game_batting_sequence')
      .select('results')
      .eq('game_id', gameId)
      .eq('player_card_id', playerCardId)
      .maybeSingle();

    if (error) {
      console.error('[SportStatsService] Error fetching game batting sequence:', error);
      throw error;
    }
    if (!data?.results || !Array.isArray(data.results)) return null;
    return data.results as string[];
  }

  /**
   * Delete the batting sequence for a player in a game (e.g. when clearing batting data).
   */
  async deleteGameBattingSequence(gameId: string, playerCardId: string): Promise<void> {
    const { error } = await supabase
      .from('game_batting_sequence')
      .delete()
      .eq('game_id', gameId)
      .eq('player_card_id', playerCardId);

    if (error) {
      console.error('[SportStatsService] Error deleting game batting sequence:', error);
      throw error;
    }
  }
}

export const sportStatsService = new SportStatsService();

